/**
 * AdminContacts — Phase A-6
 * 3 onglets : Messages reçus / Notifications broadcasts / Newsletter.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Mail, Archive, CheckCircle2, Send, Megaphone, Users as UsersIcon,
  Search, Trash2, Download, RefreshCw, AlertCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  fetchContactMessages, fetchContactDetail, patchContact, replyContact,
  fetchBroadcasts, sendBroadcast,
  fetchSubscribers, toggleSubscriber, deleteSubscriber, sendNewsletter,
} from "@/lib/admin-api";
import { api } from "@/lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR") : "—");
const fmtNum = (n) => (n ?? 0).toLocaleString("fr-FR");

const STATUS_BADGE = {
  unread:   { label: "🔵 Non lu", cls: "bg-blue-600 text-white" },
  read:     { label: "⚪ Lu",     cls: "bg-slate-500 text-white" },
  replied:  { label: "✅ Répondu", cls: "bg-emerald-600 text-white" },
  archived: { label: "🗂️ Archivé", cls: "bg-zinc-500 text-white" },
};
const TYPE_LABEL = {
  contact: "Contact",
  demo_request: "Démo",
  bug_report: "Bug",
  partnership: "Partenariat",
  other: "Autre",
};

const REPLY_TEMPLATES = [
  {
    name: "Accusé de réception",
    body: "Bonjour,\n\nMerci pour votre message. Nous l'avons bien reçu et reviendrons vers vous dans les 48 heures.\n\nCordialement,\nL'équipe VITAE-PUBLICA™",
  },
  {
    name: "Démo confirmée",
    body: "Bonjour,\n\nNous confirmons la planification de la démo VITAE-PUBLICA™. Un membre de notre équipe vous contactera sous 24 h pour caler un créneau.\n\nCordialement,\nL'équipe VITAE-PUBLICA™",
  },
  {
    name: "Bug — pris en charge",
    body: "Bonjour,\n\nMerci pour ce retour. Le problème a été consigné et assigné à notre équipe technique. Nous vous tiendrons informé(e) dès que le correctif sera déployé.\n\nCordialement,\nL'équipe VITAE-PUBLICA™",
  },
];

export default function AdminContacts() {
  const [tab, setTab] = useState("inbox");
  return (
    <div className="space-y-4" data-testid="admin-contacts-page">
      <div className="flex items-center gap-2">
        <Mail className="h-6 w-6" />
        <h1 className="text-2xl font-bold">Contacts &amp; Communications</h1>
      </div>

      <div className="flex gap-2 border-b">
        {[
          ["inbox", "Messages reçus", Mail],
          ["broadcasts", "Notifications", Megaphone],
          ["newsletter", "Newsletter", UsersIcon],
        ].map(([k, l, Icon]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-4 py-2 text-sm transition ${
              tab === k ? "border-b-2 border-red-700 font-semibold text-red-700 dark:text-red-400"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`contacts-tab-${k}`}
          >
            <Icon className="h-4 w-4" />
            {l}
          </button>
        ))}
      </div>

      {tab === "inbox" && <InboxTab />}
      {tab === "broadcasts" && <BroadcastsTab />}
      {tab === "newsletter" && <NewsletterTab />}
    </div>
  );
}

/* ==========================================================================
   INBOX
   ========================================================================== */
function InboxTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState({ type: "", status: "" });
  const [loading, setLoading] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = { page: 1, limit: 100 };
      if (filter.type) params.type = filter.type;
      if (filter.status) params.status = filter.status;
      const r = await fetchContactMessages(params);
      setRows(r.data || []);
      setTotal(r.total || 0);
      setUnread(r.unread || 0);
    } catch { toast.error("Erreur chargement messages."); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function open(id) {
    try {
      const m = await fetchContactDetail(id);
      setSelected(m);
      load();
    } catch { toast.error("Erreur chargement message."); }
  }
  async function archive(id) {
    try { await patchContact(id, { status: "archived" }); toast.success("Archivé."); load(); setSelected(null); }
    catch { toast.error("Échec."); }
  }
  async function saveNotes(id, notes) {
    try { await patchContact(id, { admin_notes: notes }); toast.success("Notes enregistrées."); }
    catch { toast.error("Échec."); }
  }

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-[380px_1fr]">
      {/* List */}
      <Card className="max-h-[75vh] overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">
              Boîte de réception
              {unread > 0 && <Badge className="ml-2 bg-red-700 text-white">{unread} non lus</Badge>}
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={load} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
          <div className="mt-2 flex gap-2">
            <select className="flex-1 rounded border px-2 py-1 text-xs" value={filter.type}
              onChange={(e) => setFilter((f) => ({ ...f, type: e.target.value }))}
              data-testid="inbox-filter-type">
              <option value="">Tous types</option>
              {Object.entries(TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <select className="flex-1 rounded border px-2 py-1 text-xs" value={filter.status}
              onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
              data-testid="inbox-filter-status">
              <option value="">Tous statuts</option>
              <option value="unread">Non lus</option>
              <option value="read">Lus</option>
              <option value="replied">Répondus</option>
              <option value="archived">Archivés</option>
            </select>
          </div>
        </CardHeader>
        <CardContent className="overflow-y-auto p-0" style={{ maxHeight: "60vh" }}>
          <ul data-testid="inbox-list">
            {rows.map((m) => (
              <li key={m.id}
                className={`cursor-pointer border-b px-3 py-2 hover:bg-muted/50 ${selected?.id === m.id ? "bg-muted/60" : ""} ${m.status === "unread" ? "font-semibold" : ""}`}
                onClick={() => open(m.id)}
                data-testid={`inbox-item-${m.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm">{m.sender_name}</span>
                  <span className="shrink-0 text-[10px] text-muted-foreground">{fmtDate(m.created_at).slice(0, 16)}</span>
                </div>
                <div className="truncate text-xs text-muted-foreground">{m.subject}</div>
                <div className="mt-1 flex items-center gap-1">
                  <Badge variant="outline" className="text-[9px]">{TYPE_LABEL[m.type] || m.type}</Badge>
                  <Badge className={`text-[9px] ${STATUS_BADGE[m.status]?.cls || "bg-slate-500 text-white"}`}>
                    {STATUS_BADGE[m.status]?.label || m.status}
                  </Badge>
                </div>
              </li>
            ))}
            {rows.length === 0 && <li className="p-6 text-center text-xs text-muted-foreground">Aucun message.</li>}
          </ul>
        </CardContent>
      </Card>

      {/* Detail */}
      <Card className="max-h-[75vh] overflow-hidden">
        {!selected ? (
          <CardContent className="flex h-48 items-center justify-center text-sm text-muted-foreground">
            Sélectionnez un message à gauche.
          </CardContent>
        ) : (
          <>
            <CardHeader className="border-b pb-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-base">{selected.subject}</CardTitle>
                  <div className="mt-1 text-xs">
                    <span className="font-semibold">{selected.sender_name}</span>{" "}
                    &lt;<span className="font-mono">{selected.sender_email}</span>&gt;
                    {selected.sender_organization && <> · {selected.sender_organization}</>}
                  </div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground">{fmtDate(selected.created_at)}</div>
                </div>
                <Badge className={STATUS_BADGE[selected.status]?.cls || "bg-slate-500 text-white"}>
                  {STATUS_BADGE[selected.status]?.label || selected.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 overflow-y-auto p-4" style={{ maxHeight: "55vh" }}>
              <div className="whitespace-pre-wrap rounded border bg-muted/30 p-3 text-sm" data-testid="msg-body">
                {selected.message}
              </div>
              <div>
                <Label className="text-xs">Notes admin (privées)</Label>
                <Textarea
                  defaultValue={selected.admin_notes || ""}
                  placeholder="Notes internes (non envoyées)…"
                  onBlur={(e) => saveNotes(selected.id, e.target.value)}
                  rows={3}
                  data-testid="msg-notes"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setReplyOpen(true)} data-testid="msg-reply-btn">
                  <Send className="mr-1 h-3.5 w-3.5" /> Répondre
                </Button>
                <Button variant="outline" onClick={() => archive(selected.id)} data-testid="msg-archive-btn">
                  <Archive className="mr-1 h-3.5 w-3.5" /> Archiver
                </Button>
                {selected.type === "demo_request" && (
                  <Button variant="outline" onClick={() => patchContact(selected.id, { admin_notes: (selected.admin_notes || "") + "\n[DÉMO ACCORDÉE]" }).then(() => toast.success("Démo marquée accordée."))} data-testid="msg-demo-btn">
                    <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Démo accordée
                  </Button>
                )}
              </div>
            </CardContent>
            {replyOpen && (
              <ReplyModal
                msg={selected}
                onClose={() => setReplyOpen(false)}
                onSent={() => { setReplyOpen(false); open(selected.id); }}
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function ReplyModal({ msg, onClose, onSent }) {
  const [subject, setSubject] = useState(`Re: ${msg.subject}`);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  async function submit() {
    if (!body.trim()) { toast.error("Le corps du message est requis."); return; }
    setSending(true);
    try {
      await replyContact(msg.id, { subject, body });
      toast.success("Réponse envoyée.");
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi échoué.");
    } finally { setSending(false); }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="reply-modal">
        <DialogHeader>
          <DialogTitle>Répondre à {msg.sender_name}</DialogTitle>
          <DialogDescription>{msg.sender_email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Template</Label>
            <select className="w-full rounded border px-2 py-1 text-sm"
              onChange={(e) => {
                const t = REPLY_TEMPLATES[Number(e.target.value)];
                if (t) setBody(t.body);
              }}
              data-testid="reply-template">
              <option value="">— Choisir un template —</option>
              {REPLY_TEMPLATES.map((t, i) => <option key={i} value={i}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Objet</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="reply-subject" />
          </div>
          <div>
            <Label className="text-xs">Corps du message</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} data-testid="reply-body"
              placeholder="Votre réponse…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={sending} data-testid="reply-send">
            <Send className="mr-1 h-4 w-4" /> {sending ? "Envoi…" : "Envoyer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==========================================================================
   BROADCASTS
   ========================================================================== */
function BroadcastsTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ title: "", message: "", target: "all", target_user_ids: "" });
  const [sending, setSending] = useState(false);

  async function load() {
    try { setRows(await fetchBroadcasts()); }
    catch { toast.error("Erreur."); }
  }
  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) return;
    setSending(true);
    try {
      const payload = { title: form.title, message: form.message, target: form.target };
      if (form.target === "specific" && form.target_user_ids) {
        payload.target_user_ids = form.target_user_ids.split(",").map((s) => s.trim()).filter(Boolean);
      }
      const r = await sendBroadcast(payload);
      toast.success(`Notification envoyée à ${r.recipients} utilisateur(s).`);
      setForm({ title: "", message: "", target: "all", target_user_ids: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec."); }
    finally { setSending(false); }
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Nouvelle notification</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Titre (max 100)</Label>
              <Input maxLength={100} value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                data-testid="broadcast-title" required />
              <div className="text-right text-[10px] text-muted-foreground">{form.title.length}/100</div>
            </div>
            <div>
              <Label className="text-xs">Message (max 500)</Label>
              <Textarea maxLength={500} rows={4} value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                data-testid="broadcast-message" required />
              <div className="text-right text-[10px] text-muted-foreground">{form.message.length}/500</div>
            </div>
            <div>
              <Label className="text-xs">Cible</Label>
              <select className="w-full rounded border px-2 py-1 text-sm" value={form.target}
                onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))}
                data-testid="broadcast-target">
                <option value="all">Tous les utilisateurs</option>
                <option value="admins">Admins seulement</option>
                <option value="analysts">Analystes</option>
                <option value="specific">Utilisateurs spécifiques (IDs)</option>
              </select>
            </div>
            {form.target === "specific" && (
              <div>
                <Label className="text-xs">IDs utilisateurs (séparés par virgule)</Label>
                <Input value={form.target_user_ids}
                  onChange={(e) => setForm((f) => ({ ...f, target_user_ids: e.target.value }))}
                  placeholder="uuid-1, uuid-2, …" data-testid="broadcast-ids" />
              </div>
            )}
            <Button type="submit" disabled={sending} data-testid="broadcast-send">
              <Send className="mr-1 h-4 w-4" /> {sending ? "Envoi…" : "Envoyer"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Historique ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-xs" data-testid="broadcasts-history">
            <thead>
              <tr className="border-b text-left uppercase text-muted-foreground">
                <th className="px-3 py-2">Titre</th>
                <th className="px-3 py-2">Cible</th>
                <th className="px-3 py-2 text-right">Dest.</th>
                <th className="px-3 py-2 text-right">Lus</th>
                <th className="px-3 py-2">Envoyé</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b.id} className="border-b">
                  <td className="px-3 py-2 font-semibold">{b.title}</td>
                  <td className="px-3 py-2"><Badge variant="outline">{b.target}</Badge></td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(b.target_count)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtNum(b.read_count)}</td>
                  <td className="px-3 py-2 text-[10px]">{fmtDate(b.sent_at)}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">Aucun broadcast.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

/* ==========================================================================
   NEWSLETTER
   ========================================================================== */
function NewsletterTab() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [composer, setComposer] = useState({ open: false, subject: "", body_html: "", preview: false });

  async function load() {
    setLoading(true);
    try {
      const r = await fetchSubscribers({ page, limit: 50 });
      setRows(r.data || []);
      setTotal(r.total || 0);
      setStats({ total: r.total || 0, active: r.total_active || 0, inactive: r.total_inactive || 0 });
    } catch { toast.error("Erreur."); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);

  async function toggle(id, active) {
    try { await toggleSubscriber(id, !active); toast.success(active ? "Désactivé." : "Activé."); load(); }
    catch { toast.error("Échec."); }
  }
  async function remove(id) {
    if (!confirm("Supprimer définitivement cet abonné ?")) return;
    try { await deleteSubscriber(id); toast.success("Supprimé."); load(); }
    catch { toast.error("Échec."); }
  }
  async function exportCsv() {
    try {
      const res = await api.get("/admin/newsletter/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "newsletter_subscribers.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export échoué."); }
  }
  async function send() {
    if (!composer.subject.trim() || !composer.body_html.trim()) return;
    if (!confirm(`Vous allez envoyer à ${stats.active} abonné(s) actif(s). Continuer ?`)) return;
    try {
      const r = await sendNewsletter({ subject: composer.subject, body_html: composer.body_html, confirm: true });
      toast.success(`Envoyé : ${r.sent}/${r.total}${r.failed ? ` (${r.failed} échecs)` : ""}`);
      setComposer({ open: false, subject: "", body_html: "", preview: false });
    } catch (err) { toast.error(err?.response?.data?.detail || "Échec."); }
  }

  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border bg-muted/30 p-4" data-testid="nl-kpi-total">
          <div className="text-[11px] uppercase text-muted-foreground">Total abonnés</div>
          <div className="mt-1 font-mono text-2xl font-bold">{fmtNum(stats.total)}</div>
        </div>
        <div className="rounded-lg border bg-emerald-50 p-4 dark:bg-emerald-950/20" data-testid="nl-kpi-active">
          <div className="text-[11px] uppercase text-muted-foreground">Actifs</div>
          <div className="mt-1 font-mono text-2xl font-bold text-emerald-700 dark:text-emerald-400">{fmtNum(stats.active)}</div>
        </div>
        <div className="rounded-lg border bg-muted/20 p-4" data-testid="nl-kpi-inactive">
          <div className="text-[11px] uppercase text-muted-foreground">Inactifs</div>
          <div className="mt-1 font-mono text-2xl font-bold text-muted-foreground">{fmtNum(stats.inactive)}</div>
        </div>
      </div>

      <div className="flex gap-2">
        <Button onClick={() => setComposer((c) => ({ ...c, open: true }))} data-testid="nl-compose-btn">
          <Send className="mr-1 h-4 w-4" /> Composer une newsletter
        </Button>
        <Button variant="outline" onClick={exportCsv} data-testid="nl-export-btn">
          <Download className="mr-1 h-4 w-4" /> Export CSV
        </Button>
        <Button variant="ghost" size="icon" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-xs" data-testid="subscribers-table">
            <thead>
              <tr className="border-b text-left uppercase text-muted-foreground">
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Nom</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Inscrit</th>
                <th className="px-3 py-2">Statut</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="border-b">
                  <td className="px-3 py-2 font-mono">{s.email}</td>
                  <td className="px-3 py-2">{s.name || "—"}</td>
                  <td className="px-3 py-2"><Badge variant="outline">{s.source || "—"}</Badge></td>
                  <td className="px-3 py-2 text-[10px]">{fmtDate(s.subscribed_at)}</td>
                  <td className="px-3 py-2">
                    {s.is_active
                      ? <Badge className="bg-emerald-600 text-white">Actif</Badge>
                      : <Badge className="bg-muted text-muted-foreground">Inactif</Badge>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => toggle(s.id, s.is_active)} data-testid={`sub-toggle-${s.id}`}>
                      {s.is_active ? "Désactiver" : "Activer"}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`sub-del-${s.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Aucun abonné.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {total > 50 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Préc.</Button>
          <span className="text-xs">Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Suiv.</Button>
        </div>
      )}

      {composer.open && (
        <Dialog open onOpenChange={() => setComposer({ open: false, subject: "", body_html: "", preview: false })}>
          <DialogContent className="max-w-2xl" data-testid="nl-composer">
            <DialogHeader>
              <DialogTitle>Nouvelle newsletter</DialogTitle>
              <DialogDescription>Envoi à {stats.active} abonné(s) actif(s).</DialogDescription>
            </DialogHeader>
            {!composer.preview ? (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Objet</Label>
                  <Input value={composer.subject} data-testid="nl-subject"
                    onChange={(e) => setComposer((c) => ({ ...c, subject: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Corps (HTML simple accepté : &lt;b&gt; &lt;i&gt; &lt;p&gt; &lt;a&gt; &lt;ul&gt; &lt;li&gt;)</Label>
                  <Textarea rows={10} value={composer.body_html} data-testid="nl-body"
                    onChange={(e) => setComposer((c) => ({ ...c, body_html: e.target.value }))} />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="rounded border bg-amber-50 p-3 text-xs dark:bg-amber-950/20">
                  <AlertCircle className="mr-1 inline h-3.5 w-3.5" />
                  Aperçu — le template VITAE-PUBLICA™ sera appliqué à l'envoi.
                </div>
                <div className="rounded border bg-muted/30 p-3 text-sm font-bold">
                  {composer.subject}
                </div>
                <div className="rounded border bg-background p-4 text-sm"
                  dangerouslySetInnerHTML={{ __html: composer.body_html }} />
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setComposer({ open: false, subject: "", body_html: "", preview: false })}>Annuler</Button>
              {!composer.preview ? (
                <Button variant="outline" onClick={() => setComposer((c) => ({ ...c, preview: true }))} data-testid="nl-preview-btn">
                  Aperçu
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setComposer((c) => ({ ...c, preview: false }))}>Modifier</Button>
              )}
              <Button onClick={send} disabled={!composer.preview} data-testid="nl-send-btn">
                <Send className="mr-1 h-4 w-4" /> Envoyer à {stats.active}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
